<template>
  <v-card flat>
    <v-card-item>
      <template v-slot:prepend>
        <v-avatar color="secondary" size="small">
          <v-icon icon="mdi-bullseye-arrow"></v-icon>
        </v-avatar>
      </template>
      <v-card-title class="text-subtitle-1 text-primary">
        {{ hideTargetValue ? $t("labels.title-unit-measure") : $t("labels.title-target") }}
      </v-card-title>
      <v-card-subtitle class="text-caption">
        {{ hideTargetValue ? $t("strings.unit-measure-hint") : $t("strings.target-hint") }}
        <v-tooltip activator="parent" location="top">
          {{ hideTargetValue ? $t("strings.unit-measure-hint") : $t("strings.target-hint") }}
        </v-tooltip>
      </v-card-subtitle>
    </v-card-item>
    <v-card-text class="pt-2">
      <v-row>
        <v-col cols="5" md="4" v-if="!hideTargetValue">
          <template v-if="valueType === 'QUALITATIVE'">
            <v-autocomplete v-model="targetValue" :items="Array.isArray(allowedValues) ? allowedValues : []"
              :label="$t('labels.value')" variant="outlined" density="compact" name="targetValue" required
              hide-details="auto" @update:modelValue="onSelectStringValue"
              @blur="touchedTarget = true"></v-autocomplete>
          </template>
          <template v-else>
            <v-text-field v-model="targetValue" :label="$t('labels.value')" variant="outlined" :rules="numberRules"
              required density="compact" name="targetValue" hide-details="auto"
              @blur="touchedTarget = true"></v-text-field>
          </template>
        </v-col>
        <v-col :cols="hideTargetValue ? 12 : 7" :md="hideTargetValue ? 12 : 5" v-if="valueType !== 'QUALITATIVE'">
          <v-autocomplete v-model="selectedMeasure" variant="outlined" :items="unitMeasuresArray" density="compact"
            :label="$t('labels.unit-measure')" name="unitMeasure" required :rules="measureRules"
            :disabled="disabledUnitMeasure" hide-details="auto" @blur="touchedMeasure = true"></v-autocomplete>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>
<script>
export default {
  props: [
    "symbolsUnitsMeasures",
    "parentTargetValue",
    "parentUnitMeasure",
    "parentValidFields",
    "realm",
    // new props
    "valueType",
    "allowedValues",
    "hideTargetValue", 
    "disabledUnitMeasure"
  ],
  emits: ["writeTargetValue", "writeUnitMeasure"],
  data() {
    return {
      touchedTarget: false,
      touchedMeasure: false,
      numberRules: [
        (v) => {
          this.formatTargetValue(v);
          return true; // Ensure this rule always returns true
        },
        (v) => !this.touchedTarget || !!v || this.$t("labels.required"), // only validate required after touch
        (v) =>
          !this.touchedTarget ||
          !["-", "-0", "-0.", "-."].includes(v) ||
          this.$t("strings.number-hint"),
      ],
      measureRules: [
        (v) => !this.touchedMeasure || !!v || this.$t("labels.required"),
      ],
      // keep a string for the input field; conversions to Number happen when emitting
      targetValue: this.parentTargetValue != null ? String(this.parentTargetValue) : "",
      selectedMeasure: this.parentUnitMeasure,
    };
  },
  mounted() {
    this.$nextTick(() => {
      // Ensure displayed field is string; if parent provides a Number for quantitative, convert to string
      if (this.valueType !== "QUALITATIVE") {
        this.targetValue =
          this.parentTargetValue != null ? String(this.parentTargetValue) : "";
      } else {
        this.targetValue = this.parentTargetValue != null ? this.parentTargetValue : "";
      }
      this.selectedMeasure = this.parentUnitMeasure;
    });
  },

  watch: {
    selectedMeasure(value) {
      this.$emit("writeUnitMeasure", value);
    },
    parentTargetValue(v) {
      // update displayed value as string; keep numeric parent as Number but display as string
      if (this.valueType !== "QUALITATIVE") {
        this.targetValue = v != null ? String(v) : "";
      } else {
        this.targetValue = v != null ? v : "";
      }
    },
    valueType(v) {
      // when switching to QUALITATIVE type, clear unit measure as it's not applicable
      if (v === "QUALITATIVE") {
        this.selectedMeasure = null;
        this.$emit("writeUnitMeasure", null);
      }
    },
  },
  computed: {
    unitMeasuresArray() {
      return this.createUnitArray(this.symbolsUnitsMeasures);
    },
  },
  methods: {
    createUnitArray(unitSymbols) {
      // Guard clause: return empty array if unitSymbols is null/undefined
      if (!unitSymbols) {
        return [{ title: this.$t(`measures.NONE`), value: "None" }];
      }

      const unitArray = Object.entries(unitSymbols).map(([key, value]) => {
        const title = this.$t(`measures.${key}`);
        return {
          title: `${title} - ${value}`,
          value: key,
        };
      });
      unitArray.unshift({ title: this.$t(`measures.NONE`), value: "None" });
      return unitArray;
    },

    formatTargetValue(value) {
      if (value == null) {
        value = ""; // Ensure value is a string if null or undefined
      } else if (typeof value !== "string") {
        value = value.toString(); // Convert non-string values to string
      }

      // Replace invalid characters with an empty string
      value = value.replace(/[^0-9,.-]/g, "");
      // Replace all '.' with ','
      value = value.replace(/,/g, ".");
      // Ensure the dot is not at the beginning
      if (value.startsWith(".")) {
        value = value.substring(1);
      }
      // Ensure the dot is not immediately after a minus sign
      value = value.replace(/-\./, "-");
      // Ensure '-' only appears at the start
      value = value.replace(/(?!^)-/g, "");
      // Split the value by commas to check positions of hyphens
      let parts = value.split(".");
      // Keep only the first comma
      if (parts.length > 2) {
        parts = [parts.slice(0, 2).join(".")];
      }
      for (let i = 0; i < parts.length; i++) {
        if (parts[i].indexOf("-") > 0) {
          // If hyphen is not at the start, remove it
          parts[i] = parts[i].replace("-", "");
        }
      }
      // Join the parts back together with commas
      value = parts.join(".");

      this.targetValue = value;
      // Emit typed value: Number for quantitative, String for qualitative
      if (this.valueType === "QUALITATIVE") {
        this.$emit("writeTargetValue", value);
      } else {
        const n = Number(value);
        this.$emit("writeTargetValue", Number.isFinite(n) ? n : null);
      }
    },
    onSelectStringValue(val) {
      // when selecting an allowed string value
      this.targetValue = val;
      // allowed values are strings (qualitative)
      this.$emit("writeTargetValue", val);
    },
  },
};
</script>
